import type { ReactNode } from 'react';
import { DateCell } from './DateCell';
import { TextCell } from './TextCell';
import type { OtherInfoData } from './OtherInfoModal';

/**
 * The shape both table shells accept.
 *
 * `ColumnDef` and `DataColumn` are structurally compatible by design — that is what lets
 * `<IndexListView>` take a panel's columns directly — so the audit trio is declared once in
 * the narrower shape and assigned into either.
 */
export interface AuditColumn<T> {
  key: string;
  header: string;
  width: number;
  render: (row: T) => ReactNode;
}

/**
 * `Created · Created by · Latest update`, appended by the SHELL from the `otherInfo` every
 * surface already supplies — never declared by a page or a panel.
 *
 * **It lived in `<IndexPage>` alone, and `<DataTablePanel>` had no equivalent**, so all nine
 * personnel section panels hand-rolled a `createdAt` column of their own and not one of them
 * had an update column. Updating a record showed nothing anywhere a reader looks: the panel
 * table had no column for it, and `<RowPreviewModal>` is built from those same columns, so
 * the detail modal could not show it either.
 *
 * That is the rule this file exists to satisfy — when a block is hand-rolled at a second call
 * site it belongs in the shell — and the cost of it living in one shell was nine partial
 * copies, all missing the same half.
 *
 * Three details the copies got wrong or left out, kept here so they cannot drift again:
 *
 * - **Not sortable.** The sort allow-list is per-module on the server, and a control that
 *   silently fell back to the default order is worse than no control.
 * - **`Created by` is `size="xs"`**, matching the 13px `<DateCell>` beside it: the two are one
 *   fact split across two columns and must not read as two weights of information.
 * - **`Latest update` falls back to BLANK, not an em dash.** A record never touched since it
 *   was created has no update, and a dash there would claim one that did not happen. `Created`
 *   takes the dash, because every record has a creation and a missing one is a fault.
 * - **A row with NO provenance renders all three blank**, because `otherInfo` may return null
 *   for a row that is DERIVED rather than stored. Such a page passes `auditColumns={false}`
 *   and these are never built at all; tolerated here rather than assumed away, so a page that
 *   sets one and forgets the other loses three columns instead of crashing its whole table.
 */
export function buildAuditColumns<T>(otherInfo: (row: T) => OtherInfoData | null): AuditColumn<T>[] {
  return [
    {
      key: 'createdAt',
      header: 'Created',
      width: 175,
      render: (row: T) => <DateCell value={otherInfo(row)?.createdAt ?? null} fallback="—" />,
    },
    {
      key: 'createdByName',
      header: 'Created by',
      width: 190,
      render: (row: T) => <TextCell value={otherInfo(row)?.createdByName ?? null} mono size="xs" />,
    },
    {
      key: 'updatedAt',
      header: 'Latest update',
      width: 175,
      render: (row: T) => <DateCell value={otherInfo(row)?.updatedAt ?? null} fallback="" />,
    },
  ];
}
